import { useEffect, useState } from 'react';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';

interface HeroSlide {
    readonly src: string;
    readonly alt: TranslationKey;
}

const SLIDES: readonly HeroSlide[] = [
    { src: '/media/images/hero-slide-1.png', alt: 'site.hero.slide1_alt' },
    { src: '/media/images/hero-slide-2.jpeg', alt: 'site.hero.slide2_alt' }
];

const AUTOPLAY_MS = 4500;

/**
 * The two photographs that crossfade in the hero, with the dots under them.
 *
 * Three things stop the rotation, and each of them is a real complaint about a carousel: a pointer
 * resting on it, a hidden tab (`setInterval` keeps firing there, so a visitor returning would find
 * a slide chosen while they were elsewhere), and a visitor who has asked their system for reduced
 * motion, for whom it never starts at all.
 *
 * The first image loads eagerly and the second does not: the first is the largest thing above the
 * fold on a telephone, and deferring it would trade a fast paint for an empty hero.
 */
export function HeroCarousel() {
    const { t } = useTranslation();
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        const onVisibility = () => setHidden(document.hidden);

        document.addEventListener('visibilitychange', onVisibility);

        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    useEffect(() => {
        if (paused || hidden || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const timer = window.setInterval(
            () => setIndex((current) => (current + 1) % SLIDES.length),
            AUTOPLAY_MS
        );

        // `index` is a dependency so that choosing a dot restarts the wait rather than leaving the
        // visitor with whatever was left of the previous one.
        return () => window.clearInterval(timer);
    }, [paused, hidden, index]);

    return (
        <div
            className="hero-carousel"
            aria-roledescription="carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {SLIDES.map((slide, position) => (
                <img
                    key={slide.src}
                    src={slide.src}
                    alt={t(slide.alt)}
                    className={
                        position === index ? 'hero-photo hero-slide is-active' : 'hero-photo hero-slide'
                    }
                    loading={position === 0 ? 'eager' : 'lazy'}
                />
            ))}
            <div className="hero-carousel-dots" aria-label={t('site.hero.slides')}>
                {SLIDES.map((slide, position) => (
                    <button
                        key={slide.src}
                        type="button"
                        className={position === index ? 'is-active' : undefined}
                        aria-label={`${t('site.hero.slide')} ${String(position + 1)}`}
                        onClick={() => setIndex(position)}
                    />
                ))}
            </div>
        </div>
    );
}
